import { ChangeEvent, FC, useCallback, useRef } from "react";
import {
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

import { useAppDispatch } from "../../../../store";
import { addNotificationAction } from "../../../notifications";
import { NotificationSeverity } from "../../../notifications/shared/enums/notification-severity";
import { NotificationType } from "../../../notifications/shared/enums/notification-type";
import {
  useExportCropSeedExcelMutation,
  useImportCropSeedExcelMutation,
} from "../../store/seeds.rtkq";

const EXCEL_FILE_INPUT_ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";
const EXCEL_FILE_EXTENSIONS = [".xlsx", ".xls"];
const EXCEL_FILE_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

function isExcelFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const hasExcelExtension = EXCEL_FILE_EXTENSIONS.some((extension) =>
    fileName.endsWith(extension)
  );
  const hasValidMimeType =
    file.type.length === 0 || EXCEL_FILE_MIME_TYPES.includes(file.type);

  return hasExcelExtension && hasValidMimeType;
}

export const SeedsExcelActions: FC = () => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [exportCropSeedExcel, { isLoading: isExporting }] =
    useExportCropSeedExcelMutation();
  const [importCropSeedExcel, { isLoading: isImporting }] =
    useImportCropSeedExcelMutation();

  const showErrorNotification = useCallback(
    (message: string, error: unknown) => {
      dispatch(
        addNotificationAction({
          type: NotificationType.Snack,
          severity: NotificationSeverity.Error,
          message,
          error,
        })
      );
    },
    [dispatch]
  );

  const handleExport = useCallback(async () => {
    try {
      await exportCropSeedExcel().unwrap();
    } catch (error) {
      showErrorNotification("Ошибка экспорта", error);
    }
  }, [exportCropSeedExcel, showErrorNotification]);

  const handleImportButtonClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  }, []);

  const handleImportFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (!isExcelFile(file)) {
        showErrorNotification("Ошибка загрузки файла", new Error("Invalid Excel file"));
        return;
      }

      try {
        await importCropSeedExcel(file).unwrap();
        dispatch(
          addNotificationAction({
            type: NotificationType.Snack,
            severity: NotificationSeverity.Success,
            message: "Файл Excel импортирован",
          })
        );
      } catch (error) {
        showErrorNotification("Ошибка загрузки файла", error);
      }
    },
    [dispatch, importCropSeedExcel, showErrorNotification]
  );

  return (
    <>
      <LoadingButton
        size="small"
        color="primary"
        onClick={handleExport}
        loading={isExporting}
        loadingPosition="start"
        startIcon={<FileDownloadIcon />}
        variant="contained"
        type="button"
      >
        <span>Экспорт в Excel</span>
      </LoadingButton>
      <LoadingButton
        size="small"
        color="primary"
        onClick={handleImportButtonClick}
        loading={isImporting}
        loadingPosition="start"
        startIcon={<FileUploadIcon />}
        variant="contained"
        type="button"
      >
        <span>Импорт файла Excel</span>
      </LoadingButton>
      <input
        ref={inputRef}
        type="file"
        accept={EXCEL_FILE_INPUT_ACCEPT}
        onChange={handleImportFileChange}
        aria-label="Файл Excel для импорта семян"
        hidden
      />
    </>
  );
};
